import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { APP_TIMEZONE } from '@/lib/timezone';
import { ConsultantLayout } from '@/shared/consultant-layout';
import { ConsultantNotFoundContent } from '@/shared/global-error-screens';
import { fetchCandidateDocumentUrl } from '../document-viewer/document-viewer-query';
import {
  CandidateProfilePage as CandidateProfilePageComponent,
  type CopyKind,
  type FormationCardData,
  type JourneyBadgeKind,
  type JourneyItemData,
  type ProfileRow,
  type ProfileTabId,
} from './CandidateProfilePage.component';
import { candidateEvaluationsQueryOptions, deriveEvaluationCards } from './candidate-evaluations';
import {
  candidateJourneyQueryOptions,
  candidateProfileQueryOptions,
  isCandidateNotFound,
  type CandidateJourneyItem,
  type CandidateProfileResponse,
  type JourneyPhaseStatus,
} from './candidate-profile-query';

/** PFC-007/008/009 — Sonner com botão "Fechar". */
const COPY_TOAST: Record<CopyKind, string> = {
  email: 'E-mail copiado',
  phone: 'Telefone copiado',
  proLink: 'Link do LinkedIn copiado',
};

const PHASE_STATUS_LABEL: Record<JourneyPhaseStatus, string> = {
  completed: 'Finalizada',
  in_progress: 'Em andamento',
  not_started: 'Não realizada',
};

/**
 * Datas do perfil: aceita tanto data civil crua ("1996-08-16" — formata
 * direto, sem Date, para não deslizar um dia via UTC) quanto instante ISO
 * (lido em America/Sao_Paulo, padrão do app — ver date-timezone-standard).
 */
const DATE_BR_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: APP_TIMEZONE,
});

function formatDateBr(value: string | null): string | null {
  if (!value) return null;
  const civil = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (civil) return `${civil[3]}/${civil[2]}/${civil[1]}`;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return DATE_BR_FORMAT.format(new Date(ms));
}

/** CPF 000.000.000-00 quando são 11 dígitos e não-estrangeiro; senão cru. */
function formatCpf(document: string | null, isForeigner: boolean): string | null {
  if (!document) return null;
  const digits = document.replace(/\D/g, '');
  if (!isForeigner && digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return document;
}

/** +55 (xx) xxxxx-xxxx quando possível; senão o valor cru. */
function formatPhoneBr(phone: string | null): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('55')) digits = digits.slice(2);
  if (digits.length === 11) {
    return `+55 (${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+55 (${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

/** Só empilha a linha quando há valor (REGRA DE OURO — PFC-005). */
function pushRow(rows: ProfileRow[], label: string, value: string | null, copyKind?: CopyKind) {
  if (!value) return;
  rows.push(copyKind ? { label, value, copyKind } : { label, value });
}

function badgeOf(item: CandidateJourneyItem): JourneyBadgeKind {
  if (item.cancelled) return 'cancelled';
  if (item.rejected) return 'rejected';
  if (item.jobClosed) return 'closed';
  return 'active';
}

function toJourneyItemData(item: CandidateJourneyItem): JourneyItemData {
  let phaseLine: string | null = null;
  if (item.currentPhase) {
    phaseLine = [item.currentPhase.name, PHASE_STATUS_LABEL[item.currentPhase.status]]
      .filter(Boolean)
      .join(' / ');
  }

  let workAreaLine: string | null = null;
  if (item.workArea) {
    const location = [item.workArea.cities.join(', '), item.workArea.states.join(', ')]
      .filter((part) => part.length > 0)
      .join(' - ');
    const line = [item.workArea.title, location].filter(Boolean).join(' | ');
    workAreaLine = line.length > 0 ? line : null;
  }

  return {
    subscriptionId: item.subscriptionId,
    jobName: item.jobName ?? item.programName ?? '—',
    programName: item.programName,
    companyName: item.companyName,
    companyLogoUrl: item.companyLogoUrl,
    badge: badgeOf(item),
    subscribedAt: formatDateBr(item.createdAt),
    phaseLine,
    qualifiedAptLine: `${item.qualified ? 'Sim' : 'Não'} / ${item.apt ? 'Sim' : 'Não'}`,
    workAreaLine,
  };
}

interface DerivedProfile {
  displayName: string;
  initials: string;
  avatarUrl: string | null;
  email: string | null;
  phone: string | null;
  proLink: string | null;
  personalRows: ProfileRow[];
  addressRows: ProfileRow[];
  diversityRows: ProfileRow[];
  hasAttestation: boolean;
  hasResume: boolean;
  formations: FormationCardData[];
}

function deriveProfile(profile: CandidateProfileResponse | undefined): DerivedProfile {
  if (!profile) {
    return {
      displayName: '',
      initials: '?',
      avatarUrl: null,
      email: null,
      phone: null,
      proLink: null,
      personalRows: [],
      addressRows: [],
      diversityRows: [],
      hasAttestation: false,
      hasResume: false,
      formations: [],
    };
  }

  const { user, personal, address, diversity } = profile;
  const displayName = user.socialName ?? user.name ?? 'Candidato(a)';
  const phone = formatPhoneBr(personal.phone);

  const personalRows: ProfileRow[] = [];
  pushRow(personalRows, 'Nome social', personal.socialName);
  pushRow(personalRows, 'Nome', personal.name);
  // EU-525: isForeigner é projeção de document_type no Core — estrangeiro
  // guarda passaporte no mesmo campo `document`, então o rótulo acompanha.
  pushRow(
    personalRows,
    personal.isForeigner ? 'Passaporte' : 'CPF',
    formatCpf(personal.document, personal.isForeigner)
  );
  pushRow(personalRows, 'RG', personal.rg);
  pushRow(personalRows, 'Nome da mãe', personal.mothersName);
  pushRow(personalRows, 'Data de nascimento', formatDateBr(personal.birthDate));
  pushRow(personalRows, 'E-mail', personal.email, 'email');
  pushRow(personalRows, 'Telefone', phone, 'phone');
  pushRow(personalRows, 'Link profissional', personal.proLink, 'proLink');

  const addressRows: ProfileRow[] = [];
  pushRow(addressRows, 'País', address.countryName);
  pushRow(addressRows, 'Estado', address.stateName);
  pushRow(addressRows, 'Cidade', address.cityName ?? address.cityText);
  pushRow(addressRows, 'CEP', address.zipcode);
  pushRow(addressRows, 'Bairro', address.neighborhood);
  pushRow(addressRows, 'Logradouro', address.streetName);
  pushRow(addressRows, 'Número', address.number);
  pushRow(addressRows, 'Complemento', address.complement);

  const diversityRows: ProfileRow[] = [];
  pushRow(diversityRows, 'Gênero', diversity.gender);
  pushRow(diversityRows, 'Raça/cor', diversity.ethnicity);
  pushRow(diversityRows, 'Orientação afetiva-sexual', diversity.sexualOrientation);
  pushRow(diversityRows, 'Pessoa com deficiência', diversity.isPwd);
  pushRow(
    diversityRows,
    'Tipo de deficiência',
    diversity.pwdTypes.length > 0 ? diversity.pwdTypes.join(', ') : null
  );

  // PFC-006 — TODAS as formações viram card; título = curso ?? tipo de curso.
  const formations: FormationCardData[] = [];
  for (const formation of profile.formations) {
    const title = formation.courseName ?? formation.courseTypeName;
    if (!title) continue;
    const lines: string[] = [];
    if (formation.courseName && formation.courseTypeName) lines.push(formation.courseTypeName);
    if (formation.institutionName) lines.push(formation.institutionName);
    const formationDate = formatDateBr(formation.formationDate);
    if (formationDate) lines.push(`Data de formação ${formationDate}`);
    formations.push({ id: formation.id, title, lines });
  }

  return {
    displayName,
    initials: initialsOf(displayName),
    avatarUrl: user.avatarUrl,
    email: personal.email ?? user.email,
    phone,
    proLink: personal.proLink,
    personalRows,
    addressRows,
    diversityRows,
    hasAttestation: diversity.hasAttestation,
    hasResume: profile.hasResume,
    formations,
  };
}

/**
 * Container da tela "Perfil do Candidato" (EU-594). Duas queries
 * independentes: perfil (página) e trajetória (card lateral, com estados
 * próprios — PFC-016/031).
 */
export default function CandidateProfilePage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { userId } = useParams({ from: '/_authenticated/candidatos/$userId' });
  const { companyId } = useSearch({ from: '/_authenticated/candidatos/$userId' });

  const profileQuery = useQuery(candidateProfileQueryOptions(userId));
  const journeyQuery = useQuery(candidateJourneyQueryOptions(userId));

  // EU-686 — aba local (RN-01/04): trocar de aba NÃO recarrega a tela nem
  // refaz a query do perfil (cache do TanStack); o histórico só é buscado
  // quando a aba abre (lazy).
  const [activeTab, setActiveTab] = useState<ProfileTabId>('personal');
  const evaluationsQuery = useQuery({
    ...candidateEvaluationsQueryOptions(userId, companyId),
    enabled: activeTab === 'history',
  });
  const evaluationCards = useMemo(
    () => deriveEvaluationCards(evaluationsQuery.data?.items ?? []),
    [evaluationsQuery.data]
  );

  const derived = useMemo(() => deriveProfile(profileQuery.data), [profileQuery.data]);

  // EU-686 RN-20/22 + HAV-026 — candidato inexistente NÃO é falha de
  // carregamento: é a página não existir. Antes, o 404 caía no erro local
  // ("Não foi possível carregar o perfil" + "Tentar novamente") e a tela ficava
  // um beco sem saída — cabeçalho sem nome, abas vazias e nenhum caminho de
  // volta. A tela global assume, com o menu preservado (RN-25) porque o
  // consultor segue autenticado.
  const profileNotFound = isCandidateNotFound(profileQuery.error);

  // Foto enviada PELA PLATAFORMA vive no bucket privado e sai como bytes
  // autenticados (/candidates/:userId/files/avatar) — `<img src>` não manda
  // bearer, então baixamos o blob. Só quem entra via SSO tem `avatarUrl`
  // externa direta. Falha do avatar é acessória: cai no fallback de iniciais.
  const hasExternalAvatar = Boolean(profileQuery.data?.user.avatarUrl);
  const needsAvatarBlob = Boolean(profileQuery.data?.user.hasAvatar) && !hasExternalAvatar;
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!needsAvatarBlob) {
      setAvatarBlobUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchCandidateDocumentUrl(userId, 'avatar')
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setAvatarBlobUrl(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setAvatarBlobUrl(null);
    };
  }, [userId, needsAvatarBlob]);
  const journeyItems = useMemo(
    () => (journeyQuery.data?.items ?? []).map(toJourneyItemData),
    [journeyQuery.data]
  );

  function handleCopy(kind: CopyKind) {
    const value =
      kind === 'email' ? derived.email : kind === 'phone' ? derived.phone : derived.proLink;
    if (!value) return;
    void navigator.clipboard.writeText(value).then(() => {
      toast(COPY_TOAST[kind], { action: { label: 'Fechar', onClick: () => undefined } });
    });
  }

  /**
   * Viewers de currículo/laudo (fatia 2 da EU-594) — navegação TIPADA para
   * `/candidatos/$userId/curriculo|laudo`, preservando o `companyId` da área
   * de trabalho.
   */
  function goToViewer(segment: 'curriculo' | 'laudo') {
    void navigate({
      to: segment === 'curriculo' ? '/candidatos/$userId/curriculo' : '/candidatos/$userId/laudo',
      params: { userId },
      search: { companyId },
    });
  }

  if (profileNotFound) {
    return (
      <ConsultantLayout activeMenuId="programs">
        <ConsultantNotFoundContent />
      </ConsultantLayout>
    );
  }

  return (
    <ConsultantLayout activeMenuId="programs">
      <CandidateProfilePageComponent
        isLoading={profileQuery.isLoading}
        isError={profileQuery.isError}
        onRetry={() => void profileQuery.refetch()}
        onBack={() => router.history.back()}
        displayName={derived.displayName}
        avatarUrl={derived.avatarUrl ?? avatarBlobUrl}
        initials={derived.initials}
        hasResume={derived.hasResume}
        onOpenResume={() => goToViewer('curriculo')}
        email={derived.email}
        phone={derived.phone}
        proLink={derived.proLink}
        onCopy={handleCopy}
        personalRows={derived.personalRows}
        addressRows={derived.addressRows}
        diversityRows={derived.diversityRows}
        hasAttestation={derived.hasAttestation}
        onOpenAttestation={() => goToViewer('laudo')}
        formations={derived.formations}
        journeyLoading={journeyQuery.isLoading}
        journeyError={journeyQuery.isError}
        onJourneyRetry={() => void journeyQuery.refetch()}
        journeyItems={journeyItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        evaluationsLoading={activeTab === 'history' && evaluationsQuery.isLoading}
        evaluationsError={evaluationsQuery.isError}
        evaluationCards={evaluationCards}
      />
    </ConsultantLayout>
  );
}
